import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { onIdTokenChanged } from 'firebase/auth';
import { doc, onSnapshot, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, createUserProfileDocument, reactivateUserAndBoats, getFavoriteBoatIds } from '../firebase/firebase';
import { setCurrentUser, authIsReady } from '../actions';

export const useAuth = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    let unsubscribeFromSnapshot = null;
    const unsubscribeFromAuth = onIdTokenChanged(auth, async (user) => {
      // Clean up any existing snapshot listener
      if (unsubscribeFromSnapshot) unsubscribeFromSnapshot();

      // Dispatch authIsReady as soon as we know the auth state, don't wait for profile data.
      dispatch(authIsReady());

      if (user) {
        try {
          const userDocRef = await createUserProfileDocument(user);
          // Update last login timestamp on sign-in
          await updateDoc(userDocRef, { lastLogin: serverTimestamp() });

          const idTokenResult = await user.getIdTokenResult();

          // One-time check for reactivation on login
          const userDoc = await getDoc(userDocRef);
          const favoritesResult = await getFavoriteBoatIds();
          const favoriteBoatIds = favoritesResult.data.favoriteBoatIds || [];

          if (userDoc.exists() && userDoc.data().active === false) {
            await reactivateUserAndBoats(userDocRef);
          }

          // Listen for real-time changes to the user document
          unsubscribeFromSnapshot = onSnapshot(doc(db, 'users', user.uid), (snapShot) => {
            if (!snapShot.exists() || snapShot.data().active === false) {
              auth.signOut(); // Triggers onIdTokenChanged again with user=null
              return;
            }

            const userData = snapShot.data();

            // Handle profile completion redirect
            if (
              userData?.profileComplete === false &&
              history.location.pathname !== '/paypal/return' &&
              history.location.pathname !== '/profile/edit'
            ) {
              history.push('/profile/edit');
            }

            // DEFINITIVE FIX: Create a robust serialization helper for dates.
            // This prevents crashes if a date field is already an ISO string (e.g., from a previous Redux state)
            // instead of a Firestore Timestamp object, which would cause `.toDate()` to fail.
            const serializeDate = (dateValue) => {
              if (dateValue && typeof dateValue.toDate === 'function') {
                return dateValue.toDate().toISOString();
              }
              return dateValue || null; // Return as is if it's already a string or null
            };

            // Set user in Redux, using token claims as the source of truth for roles
            dispatch(setCurrentUser({
              id: snapShot.id,
              ...userData,
              captainLicenseVerificationDate: serializeDate(userData.captainLicenseVerificationDate),
              memberSince: serializeDate(userData.memberSince),
              dateOfBirth: serializeDate(userData.dateOfBirth),
              createdAt: serializeDate(userData.createdAt),
              lastLogin: serializeDate(userData.lastLogin),
              roles: idTokenResult.claims, // Ensures roles are always up-to-date
              favorites: favoriteBoatIds,
            }));
          });
        } catch (error) {
          auth.signOut(); // Sign out on error to prevent bad state
        }
      } else {
        // User is signed out
        dispatch(setCurrentUser(null));
      }
    });

    // Cleanup function for when the component unmounts
    return () => {
      unsubscribeFromAuth();
      if (unsubscribeFromSnapshot) unsubscribeFromSnapshot();
    };
  }, [dispatch, history]);
};
